/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useRef, ReactElement } from 'react';
import styled from '@emotion/styled';
import { getPatternModal } from '../../../style';
import Avatar from '../../../atoms/Avatar';
import Headline from '../../common/Headline';
import Button from '../../../atoms/buttons/Button';
import ButtonSet from '../../blocks/ButtonSet';
import { IsMobileViewPort } from '../../../utils/helpers';
import { trackUnlockMessage } from '../../../actions/profileTrackAction';
import useMixPanel from '../../../hooks/useMixPanel';
import { EVENT_NAMES } from '../../../constants/mixpanel';
import { useNavigate } from 'react-router-dom';
import OfferUnlockIcon from './oneTapSwipeOffer/icons/OfferUnlockIcon';
import useMandatoryWall from '../../../hooks/useMandatoryWall';
import OfferModalFooter from './OfferModalFooter';
import ModalWithPattern from 'components/common/modals/ModalWithPattern';
import { mobileContentStyle, mobileOverlayStyle } from 'components/common/CommonStyle';
import breakpoints from 'theme/material/breakpoints';
import useMediaBreakpoints from 'hooks/useMediaBreakpoints';

const Form = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const HeaderDiv = styled('div')({
  textAlign: 'center',
  marginTop: 42,

  [breakpoints.up(640)]: {
    marginTop: 12,
  },
});

const AvatarTitleDiv = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'wrap',
  justifyContent: 'center',
  margin: '0 auto',
  alignItems: 'center',
});

const TitleDiv = styled('span')(() => ({
  color: getPatternModal().titleColor,
  fontSize: 32,
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginLeft: 8,
  [breakpoints.down(639.05)]: {
    fontSize: 26,
    letterSpacing: '0.52px',
    lineHeight: '26px',
    marginBottom: 21,
  },
}));

const Content = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: '1',
  justifyContent: 'space-between',
});

const ButtonDiv = styled('div')({
  marginTop: 22,

  [breakpoints.down(639.05)]: {
    marginBottom: 10,
    marginTop: 0,
    paddingTop: 12,
    width: '100%',
  },
});

interface OfferAcceptUnlockV2Props {
  isModalOpen: boolean;
  customContentStyle?: any;
  offerAmount: any;
  avatarSrc: any;
  username: any;
  buttonID: any;
  creditCost: any;
  hashId: any;
  currentUserIsGenerous: any;
  closeModal: any;
  currentCredit: any;
  unlockMessage(hashId: any, triggerToBuy: boolean, callback: any): void;
  currencySymbol: any;
}

const OfferAcceptUnlockV2 = ({
  isModalOpen,
  customContentStyle,
  offerAmount,
  avatarSrc,
  username,
  buttonID,
  creditCost,
  hashId,
  currentUserIsGenerous,
  closeModal,
  currentCredit,
  unlockMessage,
  currencySymbol,
}: OfferAcceptUnlockV2Props): ReactElement => {
  const unlockCancelBtn = useRef(null);
  const { mixpanelData, resetMixpanelData, trackPackagesPageVisited } = useMixPanel();
  const { showMandatoryWall } = useMandatoryWall();
  const { isMobile } = useMediaBreakpoints();
  const navigate = useNavigate();

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    trackUnlockMessage();

    if (creditCost > currentCredit) {
      trackPackagesPageVisited({ Source: 'Unlock Attempt' });
      navigate('/packages');
      return;
    }

    showMandatoryWall(
      () => {
        unlockMessage(hashId, mixpanelData[EVENT_NAMES.CONVERSATION_UNLOCKED].triggerToBuy, () => {
          resetMixpanelData(EVENT_NAMES.CONVERSATION_UNLOCKED);
        });
      },
      () => undefined,
      'Unlock Offer'
    );

    closeModal();
  };

  const getHeadline = () => {
    // Your first message has been unlocked.
    const subText = currentUserIsGenerous ? (
      <p style={{ marginTop: 10 }}>
        Unlock messaging for <strong>{creditCost} Credits</strong> and start planning your date!
      </p>
    ) : (
      <p style={{ marginTop: 10 }}>Send {username} a note and start planning your date!</p>
    );

    return (
      <div>
        <span>
          You&lsquo;ve accepted <strong>{username}</strong>&lsquo;s offer of {currencySymbol.currencyInitial}
          {currencySymbol.currencyLabel}
          {offerAmount}.{' '}
        </span>
        <br />
        {subText}
      </div>
    );
  };

  return (
    <React.Fragment>
      <ModalWithPattern
        isModalOpen={isModalOpen}
        dataTestID="offer-success"
        closeBtnClassName="offer-success-modal-close"
        closeBtnDataTestId="offer-success-modal-close"
        modalContentLabel="Success Offer Modal"
        onCloseClick={closeModal}
        closeRequest={closeModal}
        customOverlayStyle={IsMobileViewPort ? mobileOverlayStyle : {}}
        customContentStyle={IsMobileViewPort ? { ...mobileContentStyle, ...customContentStyle } : customContentStyle}
        withoutBgPattern={false}
        hideCloseBtn={false}
        closeOnOverlayClick={false}
        enableTouchMove={true}
      >
        <Form>
          <HeaderDiv>
            <AvatarTitleDiv>
              <TitleDiv>Offer Accepted!</TitleDiv>
              <Avatar
                data-test-id="offer-modal-avatar"
                src={avatarSrc}
                width={isMobile ? '91px' : '100px'}
                customStyle={{
                  backgroundColor: '#ccc',
                }}
              />
            </AvatarTitleDiv>
            <Headline
              style={{
                color: getPatternModal().headerColor,
                height: IsMobileViewPort ? 'auto' : 30,
                marginTop: 20,
                marginBottom: 20,
                fontSize: 16,
                lineHeight: '21px',
                letterSpacing: '-0.32px',
              }}
              data-test-id="profile-offer-headline"
              size="SM"
            >
              {getHeadline()}
            </Headline>
          </HeaderDiv>
          <Content>
            <ButtonDiv
              // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
              marginTop={16}
            >
              <ButtonSet align="center">
                <Button
                  id={buttonID}
                  buttonType="error"
                  onClick={handleSubmit}
                  type="submit"
                  customStyle={{ fontSize: 18, padding: '16px 24px' }}
                >
                  <OfferUnlockIcon
                    // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
                    style={{ marginRight: 6 }}
                  />
                  {currentUserIsGenerous ? 'Unlock Messaging' : 'Go to Conversation'}
                </Button>
              </ButtonSet>
              <ButtonSet align="center">
                <Button
                  buttonType="chromeless"
                  buttonRef={unlockCancelBtn}
                  data-test-id={`${buttonID}Cancel`}
                  onClick={e => {
                    e.preventDefault();
                    closeModal();
                  }}
                  customStyle={{ color: '#8392A7', fontWeight: 400, fontSize: 14 }}
                >
                  {' '}
                  Close
                </Button>
              </ButtonSet>
            </ButtonDiv>
          </Content>
        </Form>
        <OfferModalFooter />
      </ModalWithPattern>
    </React.Fragment>
  );
};

export default OfferAcceptUnlockV2;
